import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiContext } from '@shared/contexts';
import { EBulkOperationStatus } from '@modules/items/types/bulkOperation.types';

type TPollingJobConfig<T extends { status: EBulkOperationStatus; errorMessage?: string }> = {
  queryKeyPrefix: string;
  endpoint: string;
  progressMessage?: string;
  successMessage?: string | ((job: T) => string);
  failureMessage?: string;
  showToast?: boolean;
  onCompleted?: (job: T) => void;
  onFailed?: (job: T) => void;
  pollingInterval?: number;
};

const DEFAULT_POLLING_INTERVAL = 2000;

export const usePollingJob = <T extends { status: EBulkOperationStatus; errorMessage?: string }>(
  config: TPollingJobConfig<T>
) => {
  const showToast = config.showToast !== false;
  const { show, showProgress, apiClient, retry, useHandleQueryError } = useContext(ApiContext);
  const [jobId, setJobId] = useState<string | null>(null);
  // The job the hook currently polls - a fetch that resolves after the job changed (cancelled,
  // replaced by a newer submit) must not fire callbacks, toasts, or clear the newer job.
  const currentJobIdRef = useRef<string | null>(null);
  currentJobIdRef.current = jobId;
  const hideProgressRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (jobId && showToast && config.progressMessage && !hideProgressRef.current) {
      hideProgressRef.current = showProgress(config.progressMessage);
    }
  }, [jobId]);

  useEffect(() => {
    return () => {
      hideProgressRef.current?.();
      hideProgressRef.current = null;
    };
  }, []);

  const hideProgress = () => {
    hideProgressRef.current?.();
    hideProgressRef.current = null;
  };

  const queryFn = async (): Promise<T | undefined> => {
    const requestedJobId = jobId;
    const { data } = await apiClient.get<T | null>(`${config.endpoint}/${requestedJobId}`);
    if (currentJobIdRef.current !== requestedJobId) return data ?? undefined;

    if (!data) {
      hideProgress();
      setJobId(null);
      return undefined;
    }

    if (data.status === EBulkOperationStatus.COMPLETED) {
      hideProgress();
      if (showToast && config.successMessage) {
        const msg = typeof config.successMessage === 'function' ? config.successMessage(data) : config.successMessage;
        show('success', msg);
      }
      config.onCompleted?.(data);
    }
    if (data.status === EBulkOperationStatus.FAILED) {
      hideProgress();
      if (showToast) {
        const message = data.errorMessage || config.failureMessage || 'Operation failed.';
        show('error', message, { timeout: 10000 });
      }
      config.onFailed?.(data);
    }
    if (data.status !== EBulkOperationStatus.CREATED) setJobId(null);
    return data;
  };

  const query = useQuery({
    queryKey: [`${config.queryKeyPrefix}-${jobId}`],
    enabled: Boolean(jobId),
    queryFn,
    retry,
    refetchInterval: ({ state }) =>
      state.data?.status === EBulkOperationStatus.CREATED
        ? (config.pollingInterval ?? DEFAULT_POLLING_INTERVAL)
        : false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (query.isError) {
      hideProgress();
      setJobId(null);
    }
  }, [query.isError]);

  useHandleQueryError(
    query.isError,
    query.error,
    `Failed to poll ${config.queryKeyPrefix} ${jobId}`,
    showToast ? 'Connection lost. Your operation may still be running.' : false
  );

  const startProgress = useCallback(() => {
    if (showToast && config.progressMessage && !hideProgressRef.current) {
      hideProgressRef.current = showProgress(config.progressMessage);
    }
    return () => {
      hideProgressRef.current?.();
      hideProgressRef.current = null;
    };
  }, []);

  return {
    isPolling: query.data?.status === EBulkOperationStatus.CREATED,
    isError: query.isError,
    error: query.error,
    data: query.data,
    setJobId,
    startProgress,
  };
};
